import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import Footer from "../../components/comunes/Footer";
import TituloPagina from "../../components/comunes/TituloPagina";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { canManageOperacionesImportacion, getRoleFromToken } from "../../utils/roleAccess";
import { importacionOperacionesApi } from "../importacion-operaciones/importacionOperacionesApi";
import "../importacion-operaciones/importacion-operaciones.css";

const formatCurrency = (value) => {
  const numericValue = Number(value || 0);
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number.isFinite(numericValue) ? numericValue : 0);
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const textValue = String(value);
  const isoMatch = textValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? textValue : date.toLocaleDateString("es-AR");
};

const formatListPreview = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "-";
  return items.slice(0, 3).join(", ");
};

const ConciliacionOperaciones = () => {
  const navigate = useNavigate();
  const { darkMode } = React.useContext(DarkModeContext);
  const [fechaLiquidacion, setFechaLiquidacion] = useState("");
  const [diasBusqueda, setDiasBusqueda] = useState(20);
  const [archivoZip, setArchivoZip] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [solicitandoClover, setSolicitandoClover] = useState(false);
  const [resultadoZip, setResultadoZip] = useState(null);
  const [resultadoClover, setResultadoClover] = useState(null);

  const token = getToken();
  const role = useMemo(() => getRoleFromToken(token), [token]);

  React.useEffect(() => {
    if (!token || !canManageOperacionesImportacion(role)) {
      navigate("/");
    }
  }, [navigate, role, token]);

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("archivo", archivoZip);
    formData.append("diasBusqueda", String(diasBusqueda || 20));
    formData.append("token", token || "");
    if (fechaLiquidacion) {
      formData.append("fechaLiquidacion", fechaLiquidacion);
    }

    return formData;
  };

  const handleAnalizarZip = async (event) => {
    event.preventDefault();
    if (!archivoZip) {
      Swal.fire("Conciliacion", "Selecciona el ZIP de liquidaciones Fiserv.", "info");
      return;
    }
    if (!fechaLiquidacion) {
      Swal.fire("Conciliacion", "Selecciona la fecha de liquidacion para buscar el SAS importado de ese dia.", "info");
      return;
    }

    try {
      setAnalizando(true);
      setResultadoClover(null);
      const data = await importacionOperacionesApi.analizarZipFiserv(buildFormData(), token);
      setResultadoZip(data);
    } catch (error) {
      Swal.fire("Conciliacion", error.message || "No se pudo analizar el ZIP.", "error");
    } finally {
      setAnalizando(false);
    }
  };

  const handleSolicitarReportesClover = async () => {
    if (!fechaLiquidacion) {
      Swal.fire("Conciliacion", "Selecciona la fecha de liquidacion.", "info");
      return;
    }

    const confirmacion = await Swal.fire({
      title: "Pedir reportes Clover",
      text: "Se van a crear jobs en auto-reportes para los comercios/fechas resueltos.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Pedir reportes",
      cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
      return;
    }

    try {
      setSolicitandoClover(true);
      const data = await importacionOperacionesApi.solicitarReportesClover({ fechaLiquidacion, diasBusqueda }, token);
      setResultadoClover(data);
      const icon = data.solicitadas > 0 ? "success" : "warning";
      Swal.fire(
        "Conciliacion",
        `Solicitados: ${data.solicitadas || 0}. Sin mapeo: ${data.sinMapeo || 0}. Con error: ${data.conError || 0}. Operaciones SAS: ${data.totalOperacionesSas || 0}.`,
        icon
      );
    } catch (error) {
      Swal.fire("Conciliacion", error.message || "No se pudieron pedir reportes Clover.", "error");
    } finally {
      setSolicitandoClover(false);
    }
  };

  return (
    <section className={darkMode ? "container-dark importacion-operaciones-page" : "container-light bg-gris importacion-operaciones-page"}>
      <div className="container py-4">
        <TituloPagina title="Conciliacion de Operaciones" />

        <Alert variant="info" className="mt-4">
          Esta seccion nueva analiza liquidaciones Fiserv contra el universo Clover sin modificar la importacion actual.
        </Alert>

        <Card className="importacion-operaciones-card mt-3">
          <Card.Body>
            <div className="importacion-operaciones-toolbar">
              <div>
                <h5 className="mb-1">Clover / Fiserv</h5>
                <div className="text-muted small">
                  Primer analisis de ZIP Fiserv: detecta PDFs de debito y propone la ventana de compras Clover a cruzar.
                </div>
              </div>
              <Badge bg="secondary" className="importacion-operaciones-badge">MVP sin escritura</Badge>
            </div>

            <Form className="mt-3" onSubmit={handleAnalizarZip}>
              <div className="row g-3">
                <div className="col-md-2">
                  <Form.Group>
                    <Form.Label>Fecha de liquidacion</Form.Label>
                    <Form.Control type="date" value={fechaLiquidacion} onChange={(event) => setFechaLiquidacion(event.target.value)} />
                  </Form.Group>
                </div>
                <div className="col-md-2">
                  <Form.Group>
                    <Form.Label>Dias hacia atras</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="60"
                      value={diasBusqueda}
                      onChange={(event) => setDiasBusqueda(event.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>ZIP Fiserv</Form.Label>
                    <Form.Control type="file" accept=".zip" onChange={(event) => {
                      setArchivoZip(event.target.files?.[0] || null);
                      setResultadoZip(null);
                      setResultadoClover(null);
                    }} />
                  </Form.Group>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <Button type="submit" variant="success" className="w-100" disabled={!archivoZip || analizando}>
                    {analizando ? <><Spinner animation="border" size="sm" className="me-2" />Analizando...</> : "Analizar ZIP"}
                  </Button>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {resultadoZip ? (
          <>
            <div className="importacion-operaciones-grid mt-4">
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">PDFs debito</div>
                  <div className="importacion-operaciones-kpi-value">{resultadoZip.totalPdfsDebito ?? 0}</div>
                </Card.Body>
              </Card>
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">Leidos</div>
                  <div className="importacion-operaciones-kpi-value">{resultadoZip.totalPdfsLeidos ?? 0}</div>
                </Card.Body>
              </Card>
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">Con error</div>
                  <div className="importacion-operaciones-kpi-value">{resultadoZip.totalPdfsConError ?? 0}</div>
                </Card.Body>
              </Card>
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">Ventas detectadas</div>
                  <div className="importacion-operaciones-kpi-value">{resultadoZip.totalOperacionesDetectadas ?? 0}</div>
                </Card.Body>
              </Card>
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">Importe Fiserv</div>
                  <div className="importacion-operaciones-kpi-value">{formatCurrency(resultadoZip.totalImporteDetectado)}</div>
                </Card.Body>
              </Card>
            </div>

            <Alert variant="light" className="mt-3 mb-0">
              Para liquidacion {formatDateOnly(resultadoZip.fechaLiquidacion)}, Fiserv define que fechas de operacion hay que pedirle a Clover. Referencia inicial: desde {formatDateOnly(resultadoZip.ventanaComprasDesde)} hasta {formatDateOnly(resultadoZip.ventanaComprasHasta)}.
              {" "}Fuente de necesidades: {resultadoZip.fuenteNecesidades === "SAS_IMPORTACION" ? `SAS importado (${resultadoZip.sasImportacion?.nombreArchivo || "archivo detectado"})` : resultadoZip.fuenteNecesidades === "SAS_ARCHIVO" ? "Excel SAS manual" : "PDFs Fiserv"}.
            </Alert>

            {resultadoZip.errores?.length ? (
              <Alert variant="warning" className="mt-3 mb-0">
                {resultadoZip.errores.join(" ")}
              </Alert>
            ) : null}

            <Card className="importacion-operaciones-card mt-3">
              <Card.Body>
                <div className="importacion-operaciones-toolbar">
                  <div>
                    <h5 className="mb-1">Reportes Clover necesarios</h5>
                    <div className="text-muted small">
                      Esta lista sale de Fiserv y marca que fechas/comercios hay que buscar o pedirle a auto-reportes.
                    </div>
                  </div>
                  <Button
                    variant="success"
                    disabled={solicitandoClover || resultadoZip.fuenteNecesidades !== "SAS_IMPORTACION"}
                    onClick={handleSolicitarReportesClover}
                  >
                    {solicitandoClover ? <><Spinner animation="border" size="sm" className="me-2" />Pidiendo...</> : "Pedir reportes Clover"}
                  </Button>
                </div>

                <div className="importacion-operaciones-table-wrap mt-3">
                  <Table responsive hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Fecha operacion</th>
                        <th>Comercio/terminal Fiserv</th>
                        <th>Marca</th>
                        <th>Operaciones</th>
                        <th>Importe Fiserv</th>
                        <th>Estado</th>
                        <th>Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultadoZip.necesidadesClover?.length ? resultadoZip.necesidadesClover.map((item, index) => (
                        <tr key={`${item.fechaOperacion || "sin-fecha"}-${item.comercioFiserv}-${item.marca}-${index}`}>
                          <td>{formatDateOnly(item.fechaOperacion)}</td>
                          <td>{item.comercioFiserv}</td>
                          <td>{item.marca}</td>
                          <td>{item.cantidadOperacionesFiserv}</td>
                          <td>{formatCurrency(item.importeTotalFiserv)}</td>
                          <td>
                            <Badge bg={item.estado === "DATOS_INSUFICIENTES" ? "warning" : "info"}>{item.estado}</Badge>
                          </td>
                          <td>{item.motivo}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7" className="importacion-operaciones-empty">Todavia no se pudieron inferir reportes Clover necesarios desde el ZIP.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {resultadoClover ? (
              <Card className="importacion-operaciones-card mt-3">
                <Card.Body>
                  <div className="importacion-operaciones-toolbar">
                    <div>
                      <h5 className="mb-1">Pedidos enviados a auto-reportes</h5>
                      <div className="text-muted small">
                        Endpoint: {resultadoClover.autoReportesEndpoint}
                      </div>
                    </div>
                    <Badge bg="success">{resultadoClover.solicitadas || 0} solicitados</Badge>
                  </div>
                  <div className="importacion-operaciones-grid mt-3">
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Total</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoClover.totalSolicitudes || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Sin mapeo</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoClover.sinMapeo || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Con error</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoClover.conError || 0}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="importacion-operaciones-table-wrap mt-3">
                    <Table responsive hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Fecha operacion</th>
                          <th>Comercio Fiserv</th>
                          <th>Comercio Clover</th>
                          <th>Cuenta</th>
                          <th>Operaciones</th>
                          <th>Estado</th>
                          <th>Job</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultadoClover.resultados?.map((item, index) => (
                          <tr key={`${item.fechaOperacion || "sin-fecha"}-${item.comercioFiserv}-${index}`}>
                            <td>{formatDateOnly(item.fechaOperacion)}</td>
                            <td>{item.comercioFiserv}</td>
                            <td>{item.comercioClover || "-"}</td>
                            <td>{item.accountKey || "-"}</td>
                            <td>{item.cantidadOperacionesFiserv}</td>
                            <td>
                              <Badge bg={item.estado === "SOLICITADO" ? "success" : item.estado === "SIN_MAPEO_CLOVER" ? "warning" : "danger"}>{item.estado}</Badge>
                            </td>
                            <td>{item.jobId || "-"}</td>
                            <td>{item.motivo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            ) : null}

            <Card className="importacion-operaciones-card mt-3 mb-5">
              <Card.Body>
                <h5 className="mb-3">Detalle PDFs Fiserv</h5>
                <div className="importacion-operaciones-table-wrap">
                  <Table responsive hover size="sm" className="mb-0 importacion-operaciones-fiserv-table">
                    <thead>
                      <tr>
                        <th>Archivo</th>
                        <th>Marca</th>
                        <th>Fecha operacion</th>
                        <th>Fecha archivo</th>
                        <th>Comercio</th>
                        <th>Ventas</th>
                        <th>Importe</th>
                        <th>Terminal</th>
                        <th>Lote</th>
                        <th>Cupon</th>
                        <th>Tarjeta</th>
                        <th>Cuotas</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultadoZip.items?.length ? resultadoZip.items.map((item, index) => (
                        <tr key={`${item.nombreArchivo}-${index}`}>
                          <td className="importacion-operaciones-col-archivo">{item.nombreArchivo}</td>
                          <td>{item.marca}</td>
                          <td>{formatDateOnly(item.fechaOperacionDetectada)}</td>
                          <td>{formatDateOnly(item.fechaLiquidacionDetectada)}</td>
                          <td>{item.comercioFiservDetectado || "-"}</td>
                          <td>{item.operacionesDetectadas ?? 0}</td>
                          <td>{item.importeDetectado ? formatCurrency(item.importeDetectado) : "-"}</td>
                          <td>{formatListPreview(item.terminalesDetectadas)}</td>
                          <td>{formatListPreview(item.lotesDetectados)}</td>
                          <td>{formatListPreview(item.cuponesDetectados)}</td>
                          <td>{formatListPreview(item.tarjetasDetectadas)}</td>
                          <td>{formatListPreview(item.cuotasDetectadas)}</td>
                          <td>
                            <Badge bg={item.error ? "danger" : "success"}>{item.error ? "ERROR" : "LEIDO"}</Badge>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="13" className="importacion-operaciones-empty">No se detectaron PDFs de debito en el ZIP.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : null}
      </div>

      <Footer />
    </section>
  );
};

export default ConciliacionOperaciones;
